import { FormattedDate, useIntl } from 'react-intl';
import { LocalDate, ZoneId, convert } from '@js-joda/core';
import { logger } from '@tock/logging';

interface Props {
  displayWeekdayAs: 'long' | 'short' | 'narrow';
  weekdayValue: number;
}

// react-intl capabilities work best going through a JS Date object.
// All we care about here is day-of-week formatting, so we just choose
// some LocalDates to work through (and to convert) that allow us
// just to convert from a 0-7 weekdayValue to a Date object with simple
// array indexing. Make sure that at all times -- use of react-intl facilities,
// when calling into js-joda convert() -- that everything is denominated
// in terms of UTC.
const REFERENCE_DATES: Date[] = [];

// Most callers use weekday values in the range 0-6 (Sun-Sat), but some use 1-7 (Mon-Sun).
// This component supports both; so we just iterate i = 0..7 inclusive.
const MIN_WEEKDAY_VALUE = 0;
const MAX_WEEKDAY_VALUE = 7;
for (let i = MIN_WEEKDAY_VALUE; i <= MAX_WEEKDAY_VALUE; i++) {
  // 2026-02-01 happens to be a Sunday.
  REFERENCE_DATES[i] = convert(LocalDate.of(2026, 2, 1 + i), ZoneId.UTC).toDate();
}

/**
 * Displays a weekday in the business or consumer locale.
 * Be sure use of this component is contained within an appropriate IntlProvider.
 */
export const DisplayWeekday: React.FC<Props> = ({ displayWeekdayAs, weekdayValue }) => {
  const intl = useIntl();
  if (weekdayValue < MIN_WEEKDAY_VALUE || weekdayValue > MAX_WEEKDAY_VALUE) {
    logger.warn(`DisplayWeekday: weekdayValue ${weekdayValue} is out of range [${MIN_WEEKDAY_VALUE}, ${MAX_WEEKDAY_VALUE}]`);
    return null;
  }
  const dateValue = REFERENCE_DATES[weekdayValue];

  const innerFormattedDate = (
    <FormattedDate
      timeZone="UTC"
      value={dateValue}
      weekday={displayWeekdayAs}
    />
  );

  if (displayWeekdayAs === 'narrow' || displayWeekdayAs === 'short') {
    return (
      // In this case, wrap the results in two layers of span. The outer one holds the aria-label;
      // the inner one holds the visible text; shorter; aria-hidden.
      // This leaves one small VO quirk: VO will read "empty group" at the end of announcing this
      // content. Frustratingly, alternatives seem to be worse, I (matthew-tock) spent a good chunk
      // of an afternoon running down potential alternatives.
      //
      // The role="text" attribute is Safari/VO-specific; it will work _in Safari in particular_
      // to prevent reading out that "empty group" suffix. Non-Safari browsers including
      // Chrome ignore it alas.
      <span role="text" aria-label={intl.formatDate(dateValue, { weekday: 'long', timeZone: 'UTC' })}>
        <span aria-hidden={true}>
          {innerFormattedDate}
        </span>
      </span>
    );
  } else {
    return innerFormattedDate;
  }
};
